import { getRouteApi } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { SelectOption } from '@/components/Select/Select'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { usePagination } from '@/hooks/usePagination'
import useRowSelection from '@/hooks/useRowSelection'
import { useSorting } from '@/hooks/useSorting'
import { toast } from '@/hooks/useToast'
import { getFilterValue } from '@/utils/get-filter-value'
import { getValue } from '@/utils/get-value'

import BulkRemoveCurriculumModal from '../components/BulkRemoveCurriculumModal'
import CurriculumsManagementListFilters from '../components/CurriculumsManagementListFilters'
import CurriculumsManagementListTable from '../components/CurriculumsManagementListTable'
import RemoveCurriculumModal from '../components/RemoveCurriculumModal'
import type { CurriculumsManagementFiltersKey } from '../constants/curriculums-management-filters'
import useDuplicateCurriculum from '../mutations/useDuplicateCurriculum'
import { useCurriculumIds } from '../queries/useCurriculumIds'
import {
  useCurriculums,
  type CurriculumListItem
} from '../queries/useCurriculums'
import type { SimpleCurriculum } from '../types/simple-curriculum'

const routeApi = getRouteApi('/_auth/curricula/')

const CurriculumsManagementListView = () => {
  const { t } = useTranslation(['curriculumsManagement', 'common'])
  const navigate = routeApi.useNavigate()
  const {
    page: _,
    pageSize: __,
    sort,
    order,
    ...filters
  } = routeApi.useSearch()
  const pagination = usePagination(routeApi)
  const sorting = useSorting(routeApi)

  const [removeCurriculumModal, setRemoveCurriculumModal] =
    useState<SimpleCurriculum | null>(null)
  const [bulkRemoveCurriculumModal, setBulkRemoveCurriculumModal] = useState<
    SimpleCurriculum[] | null
  >(null)

  const params = {
    ...pagination.pagination,
    curriculum: filters.curriculum?.map(getValue),
    educationLevel: filters.educationLevel?.map(getValue),
    aim: filters.aim,
    sort,
    order
  }

  const {
    data: curriculumList,
    isLoading: isCurriculumListLoading,
    refetch: refetchCurriculumList
  } = useCurriculums(params)
  const {
    data: curriculumIdsList,
    isLoading: isCurriculumIdsListLoading,
    refetch: refetchCurriculumIds
  } = useCurriculumIds(params)
  const {
    data: curriculumListTotalCount,
    isLoading: isCurriculumListTotalCountLoading,
    refetch: refetchCurriculumListTotalCount
  } = useCurriculumIds()

  const refetchData = () => {
    refetchCurriculumList()
    refetchCurriculumIds()
    refetchCurriculumListTotalCount()
  }

  const isLoading =
    isCurriculumListLoading ||
    isCurriculumIdsListLoading ||
    isCurriculumListTotalCountLoading

  const rowSelection = useRowSelection({
    count: curriculumIdsList?.length ?? 0,
    itemsToSelect: (curriculumIdsList ?? []).map(curriculum => curriculum.id)
  })

  const findCurriculum = (curriculumId: string) =>
    curriculumList?.list.find(({ id }) => id === curriculumId)

  const { mutate: duplicateCurriculumMutation } = useDuplicateCurriculum({
    onSuccess: ({ oldCurriculumId }) => {
      const curriculum = findCurriculum(oldCurriculumId)
      toast({
        title: t('toast.curriculum-duplicated-with-name', {
          NAME: curriculum?.name
        }),
        variant: 'success'
      })
      refetchData()
    },
    onError: curriculumId => {
      const curriculum = findCurriculum(curriculumId)
      toast({
        title: t('toast.failed-to-duplicate-curriculum', {
          NAME: curriculum?.name
        }),
        variant: 'error'
      })
    }
  })
  const handleFilterChange = (
    key: CurriculumsManagementFiltersKey,
    value?: SelectOption<string>[] | string[] | Date
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleClearAll = () => {
    navigate({
      search: {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const duplicateCurriculum = (curriculum: CurriculumListItem) => {
    duplicateCurriculumMutation({ curriculumId: curriculum.id })
  }

  const handleRemoveClick = (curriculum: SimpleCurriculum) => {
    if (!curriculum.canBeDeleted) {
      toast({
        variant: 'error',
        title: t('toast.cannot-remove.title', {
          NAME: curriculum.name
        }),
        description: t('toast.cannot-remove.description')
      })
      return
    }

    setRemoveCurriculumModal(curriculum)
  }
  const handleRemoveSuccess = () => {
    refetchData()
    rowSelection.clearSelection()
  }
  const closeRemoveCurriculumModal = () => setRemoveCurriculumModal(null)

  const handleBulkRemoveClick = (curriculumIds: string[]) => {
    const curriculums = curriculumIds
      .map(curriculumId =>
        curriculumIdsList?.find(curriculum => curriculum.id === curriculumId)
      )
      .filter(curriculum => !!curriculum)

    setBulkRemoveCurriculumModal(curriculums)
  }
  const closeBulkRemoveCurriculumModal = () =>
    setBulkRemoveCurriculumModal(null)

  return (
    <BasicLayout
      header={t('header.curricula')}
      breadcrumbs={[
        {
          title: t('header.curricula'),
          to: '/curricula'
        }
      ]}
      moduleName={t('common:header.administration')}
    >
      <CurriculumsManagementListFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearAll={handleClearAll}
      />
      <CurriculumsManagementListTable
        data={curriculumList?.list}
        count={curriculumList?.count}
        totalCount={curriculumListTotalCount?.length}
        isLoading={isLoading}
        rowSelection={rowSelection}
        onDuplicateClick={duplicateCurriculum}
        onRemoveClick={handleRemoveClick}
        onBulkRemoveClick={handleBulkRemoveClick}
        {...pagination}
        {...sorting}
      />
      {removeCurriculumModal ? (
        <RemoveCurriculumModal
          curriculum={removeCurriculumModal}
          onClose={closeRemoveCurriculumModal}
          onRemoveSuccess={handleRemoveSuccess}
        />
      ) : null}
      {bulkRemoveCurriculumModal ? (
        <BulkRemoveCurriculumModal
          curriculums={bulkRemoveCurriculumModal}
          onClose={closeBulkRemoveCurriculumModal}
          onRemoveSuccess={handleRemoveSuccess}
        />
      ) : null}
    </BasicLayout>
  )
}

export default CurriculumsManagementListView
